import React, { useEffect, useState, useCallback, useContext } from "react";
import { Drawer, Box, Typography, IconButton } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isIOS, isAndroid, mobile } from "src/utils/platformUtils";
import styles from "../../content/pages/homepage.module.css";
import CloseIcon from "@mui/icons-material/Close";
import AdContainer from "src/components/Ads/AdContainer";
import adService from "src/services/adService";
import { WidgetContext } from "src/contexts/WidgetContext";

interface FreeModelDrawerProps {
  open: boolean;
  onClose: () => void;
  // Optional: callback when ad completes and user can proceed
  onAdComplete?: () => void;
}

const FreeModelDrawer: React.FC<FreeModelDrawerProps> = ({ open, onClose, onAdComplete }) => {
  const { aiSettings } = useContext(WidgetContext);
  const [adState, setAdState] = useState<"loading" | "showing" | "completed" | "error">("loading");
  const isNative = mobile; // true for iOS/Android native apps

  // Reset ad state when drawer opens
  useEffect(() => {
    if (open) {
      setAdState("loading");

      // For native platforms, trigger AdMob rewarded video
      if (isNative && adService.isNativePlatform()) {
        triggerNativeAd();
      } else {
        // For web, the AdContainer component handles the ad display
        setAdState("showing");
      }
    }
  }, [open]);

  // Trigger AdMob rewarded video for native platforms
  const triggerNativeAd = useCallback(async () => {
    try {
      setAdState("loading");

      const result = await adService.showRewardedAd({
        onAdLoading: () => {
          console.log("[FreeModelDrawer] Native ad loading...");
        },
        onAdLoaded: () => {
          console.log("[FreeModelDrawer] Native ad loaded");
          setAdState("showing");
        },
        onAdShowing: () => {
          console.log("[FreeModelDrawer] Native ad showing");
        },
        onAdCompleted: () => {
          console.log("[FreeModelDrawer] Native ad completed");
          setAdState("completed");
          onAdComplete?.();
          // Auto-close drawer after completion
          setTimeout(() => {
            onClose();
          }, 1000);
        },
        onAdFailed: (error) => {
          console.error("[FreeModelDrawer] Native ad failed:", error);
          setAdState("error");
        },
        onAdSkipped: () => {
          console.log("[FreeModelDrawer] Native ad skipped");
          setAdState("error");
        },
        onRewardEarned: (reward) => {
          console.log("[FreeModelDrawer] Reward earned:", reward);
        },
      });

      if (!result) {
        // AdMob not available or failed to initialize
        setAdState("error");
      }
    } catch (error) {
      console.error("[FreeModelDrawer] Error triggering native ad:", error);
      setAdState("error");
    }
  }, [onAdComplete, onClose]);

  // Handle web ad completion
  const handleWebAdComplete = useCallback(() => {
    console.log("[FreeModelDrawer] Web ad completed");
    setAdState("completed");
    onAdComplete?.();
  }, [onAdComplete]);

  // Handle web ad error
  const handleWebAdError = useCallback((error: string) => {
    console.error("[FreeModelDrawer] Web ad error:", error);
    setAdState("error");
  }, []);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      hideBackdrop={true}
      sx={{
        pointerEvents: "none",
      }}
      ModalProps={{
        keepMounted: true,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true,
        sx: {
          pointerEvents: "auto", // Block background scroll when drawer is open
          top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
        },
      }}
      PaperProps={{
        sx: {
          pointerEvents: "auto",
          width: {
            xs: "100%",
            sm: "720px",
            md: "720px",
            lg: "720px",
          },
          margin: {
            xs: "0",
            sm: "0 auto",
            md: "0 auto",
            lg: "0 auto",
          },
          // height: isIOS || isAndroid ? "calc(100svh - env(safe-area-inset-top) - 56px)" : "calc(100vh - 56px)",
          height: isIOS || isAndroid
            ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
            : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"), border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
        },
      }}
    >
      <Box
        className={styles.noScrollbar}
        sx={{
          bgcolor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
          height: "100%",
          position: "relative",
          touchAction: "none",
          "& > *": {
            touchAction: "pan-y",
          },
          paddingBottom: isAndroid ? "max(env(safe-area-inset-bottom), 15px)" : "0px",
          pointerEvents: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header with title and close button */}
        <Box
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
            borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
            pb: 1,
            px: 1,
          }}
        >
          <Box sx={{ width: 40 }} /> {/* Spacer to balance the close button */}
          <Typography
            variant="h4"
            sx={{
              color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
              fontSize: "16px",
              //   fontFamily: '"Inter", sans-serif',
              flex: 1,
              textAlign: "center",
            }}
          >
            Free Model
          </Typography>
          <IconButton
            aria-label="close"
            onClick={(e) => {
              // Remove focus from button before closing to prevent aria-hidden warning
              if (e.currentTarget) {
                e.currentTarget.blur();
              }
              // Only allow closing if ad is completed or errored (not during loading/showing)
              if (adState === "completed" || adState === "error") {
                onClose();
              }
            }}
            disabled={adState === "loading" || adState === "showing"}
            sx={{
              color: adState === "loading" || adState === "showing" ? "#666666" : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
              width: 40,
              opacity: 1, // Always visible
              cursor: adState === "loading" || adState === "showing" ? "not-allowed" : "pointer",
              "&.Mui-disabled": {
                color: "#666666", // Gray color when disabled
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography
          variant="h4"
          sx={{
            color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
            fontSize: "14px",
            // fontFamily: '"Inter", sans-serif',
            pt: 2,
            textAlign: "center",
          }}
        >
          {adState === "completed"
            ? "Ad complete! Free model unlocked."
            : adState === "error"
              ? "Ad unavailable. Please try again."
              : adState === "showing"
                ? "Watch the complete ad to unlock free model"
                : "Loading ad..."}
        </Typography>
        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            pt: 2,
            px: 2,
          }}
        >
          {/* Ad Container - handles both web (AdSense) and shows placeholder for native */}
          {isNative ? (
            // Native: AdMob shows as fullscreen overlay, show status here
            <Box
              sx={{
                width: "100%",
                maxWidth: "800px",
                aspectRatio: "16/9",
                backgroundColor: `${PureLightThemeCaaS.colors.alpha.white[10]}`,
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
                gap: 2,
              }}
            >
              {adState === "loading" && (
                <>
                  <Typography variant="body1" sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[70]}` }}>
                    Loading ad...
                  </Typography>
                </>
              )}
              {adState === "showing" && (
                <Typography variant="body1" sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[70]}` }}>
                  Ad playing in fullscreen...
                </Typography>
              )}
              {adState === "completed" && (
                <Typography variant="body1" sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }}>
                  ✓ Free Model Unlocked!
                </Typography>
              )}
              {adState === "error" && (
                <Typography variant="body1" sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[50]}` }}>
                  ⚠️ Ad unavailable. Please try again.
                </Typography>
              )}
            </Box>
          ) : (
            // Web: Use AdContainer component with AdSense
            <AdContainer isActive={open && adState !== "completed"} displayDuration={5000} onAdComplete={handleWebAdComplete} onAdError={handleWebAdError} />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default FreeModelDrawer;
